import type { ComponentType } from "react";

/**
 * Single source of truth for the application routes.
 *
 * Each entry maps an application path to the dynamic `import()` of its page
 * module. `Application` builds its `React.lazy` components from this table,
 * and the sidebar uses the same table to **prefetch** a page's chunk on
 * hover/expand/click, so navigation to a never-visited page feels instant
 * instead of showing the route fallback.
 *
 * IMPORTANT: keep the path in sync with the <Route> definitions rendered by
 * Application (it renders one <Route> per key of this table).
 */
export type PageChunkLoader = () => Promise<{ default: ComponentType<any> }>;

export const pageChunkLoaders: Record<string, PageChunkLoader> = {
    // Dashboard
    '/app/dashboard': () => import("../page/Dashboard"),
    // Config
    '/app/config/period': () => import("../page/config/periode/SchoolPeriod"),
    '/app/config/schoolday': () => import("../page/config/SchoolDay"),
    '/app/config/canteenweek': () => import("../page/config/canteenweek/CanteenWeek"),
    '/app/config/categorielivre': () => import("../page/config/categorielivre/CategoreLivrePage"),
    '/app/config/childstuff': () => import("../page/config/childstuff/ChildStuffPage"),
    '/app/config/personal': () => import("../page/config/job/PersonalType"),
    '/app/config/schooldocumentype': () => import("../page/config/document/SchoolDocumentType"),
    '/app/config/folder/subfoldertype': () => import("../page/config/folder/subfolder/SubFolderType"),
    '/app/config/folder/foldertype': () => import("../page/config/folder/foldertype/FolderType"),
    '/app/config/imagerights': () => import("../page/config/imageright/ImageRights"),
    '/app/config/archiveshistoric': () => import("../page/config/archivehistoric/ArchiveHistoricPage"),
    '/app/config/nextschoolyear': () => import("../page/config/nextschoolyear/NextSchoolYear"),
    '/app/config/dish': () => import("../page/config/dish/Dish"),
    '/app/config/classroom': () => import("../page/config/classroom/Classroom"),
    '/app/config/appointment': () => import("../page/config/appointment/AppointmentType"),
    '/app/config/schooling': () => import("../page/config/scolarite/schooling/Scolarite"),
    '/app/config/schooling/schoolfees': () => import("../page/config/scolarite/additonalfees/SchoolFees"),
    '/app/config/schooling/paymentperiod': () => import("../page/config/scolarite/payment/ScolaritePaymentPeriod"),
    // Appointment
    '/app/appointment/calendar': () => import("../page/appointment/AppointmentCalendar"),
    '/app/appointment/preset': () => import("../page/appointment/Appointment"),
    // Messages / sondage / alerts
    '/app/message/list': () => import("../page/messages/center/MessageCenter"),
    '/app/sondage/question': () => import("../page/sondage/SondageCenter"),
    '/app/alert/message': () => import("../page/alertmessage/AlertMessageNotification"),
    '/app/alert/message-sent': () => import("../page/alertmessage/AlertMessageSent"),
    // Personal
    '/app/personal/list': () => import("../page/personal/Personal"),
    // Children
    '/app/child/list': () => import("../page/child/Student"),
    '/app/child/add': () => import("../page/child/AddNewChild"),
    '/app/child/edit': () => import("../page/child/EditChild"),
    '/app/child/child-day': () => import("../page/child/ChildDayAtIms"),
    // Schooling / scolarite
    '/app/schooling/schoolfees': () => import("../page/scolarite/listedesfrais/ScolariteListeFrais"),
    '/app/schooling/paiementantidate': () => import("../page/scolarite/paiementantidate/PaiementAntiDatePage"),
    '/app/schooling/payment-list': () => import("../page/scolarite/paiement/SchoolingPaymentList"),
    '/app/schooling/payment': () => import("../page/scolarite/paiement/SchoolingPayment"),
    '/app/schooling/matchingfraisoneshotenfant': () => import("../page/scolarite/matchingfraisoneshotenfant/MatchingFraisOneShotEnfant"),
    '/app/schooling/fraisoneshotenfant': () => import("../page/scolarite/fraisoneshotenfant/FraisOneShotEnfant"),
    '/app/schooling/proforma': () => import("../page/scolarite/proforma/Proforma"),
    '/app/schooling/scolaritecheancierenfant': () => import("../page/scolarite/echeancierenfant/ScolariteEcheancierEnfant"),
    '/app/schooling/groups': () => import("../page/scolarite/group/GroupPage"),
    '/app/schooling/groupsenfants': () => import("../page/scolarite/groupenfant/GroupEnfantPage"),
    '/app/schooling/scolaritecheancier': () => import("../page/scolarite/echeancier/ScolariteEcheancier"),
    '/app/schooling/scolaritedesfrais': () => import("../page/scolarite/montantdesfrais/ScolariteFraisGroup"),
    // Canteen / year menu
    '/app/year-menu/list': () => import("../page/canteen/MenuYear"),
    '/app/year-menu/calendar': () => import("../page/canteen/CalendarYearMenu"),
    // School documents
    '/app/school-document/list': () => import("../page/schooldocument/SchoolDocument"),
    // Bibliotheque
    '/app/bibliotheque/list': () => import("../page/bibliotheque/livre/Bibliotheque"),
    '/app/bibliotheque/empruntparemployer': () => import("../page/bibliotheque/emprunter/Emprunter"),
    '/app/bibliotheque/emprunt': () => import("../page/bibliotheque/listedesemprunts/Emprunt"),
    // Photos
    '/app/photo/list': () => import("../page/photo/AlbumPhoto"),
    // Mini club
    '/app/mini-club/list': () => import("../page/miniclub/MiniClub"),
    '/app/mini-club/edit': () => import("../page/miniclub/EditMiniClub"),
    // Profile
    '/app/profile': () => import("../page/profil/UserProfile"),
    // Security
    '/app/security/parent': () => import("../page/securite/parent/SecuriteParent"),
    '/app/security/employee': () => import("../page/securite/employee/SecuriteEmployee"),
    // Notifications
    '/app/notification/mesnotifs': () => import("../page/notification/MesNotifications"),
    // Marketing / products
    '/app/products/list': () => import("../page/marketing/Marketing"),
    '/app/products/edit': () => import("../page/marketing/EditProduct"),
    // Appearance / template settings (not part of the server menu)
    '/app/appearance': () => import("../page/appearance/Appearance"),
};

/** Starts downloading a page chunk without blocking. Safe to call repeatedly. */

// Prefetches are speculative: a category expand can ask for ~20 chunks at once
// (direct children). Firing them all immediately saturates the browser network
// queue (≈6 connections per origin), so a real navigation issued right after has
// to wait behind the prefetches — that is what makes the front-end "freeze" then
// finally load. We therefore run prefetches through a small FIFO pool (max N in
// flight) and never fetch the same path twice.
const MAX_CONCURRENT_PREFETCH = 4;

type PrefetchTask = () => Promise<void>;

let activePrefetchCount = 0;
const prefetchQueue: PrefetchTask[] = [];
const prefetchInFlight = new Set<string>();

const pumpPrefetchQueue = (): void => {
    while (activePrefetchCount < MAX_CONCURRENT_PREFETCH && prefetchQueue.length > 0) {
        const task = prefetchQueue.shift()!;
        activePrefetchCount += 1;
        task().finally(() => {
            activePrefetchCount -= 1;
            pumpPrefetchQueue();
        });
    }
};

export function prefetchPage(path: string | null | undefined): void {
    if (!path || prefetchInFlight.has(path)) {
        return;
    }
    const loader = pageChunkLoaders[path];
    if (!loader) {
        return;
    }
    prefetchInFlight.add(path);
    prefetchQueue.push(async () => {
        try {
            // Fire and forget — a failed speculative load must never break the UI.
            await loader();
        } catch {
            /* ignore speculative load failures */
        } finally {
            prefetchInFlight.delete(path);
        }
    });
    pumpPrefetchQueue();
}
